import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import "../AdminDashboard.css";
import refreshicon from "../../../images/refresh-2.svg";
import loader from "../../../images/Anlook.svg";
import MyContext from "../../../MyContext";
import NoIndex from "../../NoIndex";
import { CreditCard, Link2, Truck, RotateCcw ,HelpCircle} from "lucide-react";


function AdminOrderController({ Admin }) {
  // Utils
  const getOneDayBackDate = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const getCurrentDate = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  // Helpers
  const fmt = (n) =>
    n === undefined || n === null || n === "" || isNaN(Number(n))
      ? "0.00"
      : Number(n).toFixed(2);
  const safeParse = (str) => {
    try { return JSON.parse(str || "{}"); } catch { return {}; }
  };
  const parseCustomization = (custStr) => {
    const obj = typeof custStr === "string" ? safeParse(custStr) : (custStr || {});
    return Object.entries(obj); // [ [key, value], ... ]
  };

  const { baseUrl, config } = useContext(MyContext);

  // Core state
  const [Loading, setLoading] = useState(false);
  const [firstLoad, setFirstLoad] = useState(true);
  const [Clients, setClients] = useState([]);
  const [userID, setuserID] = useState(""); // client filter (table tab)
  const [Orders, setOrders] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  // Geo
  const [latitude, setLatitude] = useState(null);
  const [longitude, setLongitude] = useState(null);

  // Dates
  const [startDate] = useState(getOneDayBackDate());
  const [endDate] = useState(getCurrentDate());
  const [startDateCurrent] = useState(getOneDayBackDate());
  const [endDateCurrent] = useState(getCurrentDate());

  // Pagination (Table Tab)
  const ordersPerPage = 35;
  const [currentPage, setCurrentPage] = useState(1);

  // Tabs
  const [activeTab, setActiveTab] = useState("table"); // "table" | "search"

  // Details Modal (Table rows)
  const [showDetails, setShowDetails] = useState(false);
  const [orderDetailsLoader, setOrderDetailsLoader] = useState(false);
  const [particularOrderDetails, setParticularOrderDetails] = useState({});
  const [currentOrderRow, setCurrentOrderRow] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  // NEW: track which transaction is refunding (for spinner/disable)
  const [refundLoadingId, setRefundLoadingId] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [refundLoading, setRefundLoading] = useState(false);


  // Status options
  const ALL_STATUSES = Array.from(new Set([
    "received", "paid", "accepted", "prepared", "rejected", "refunded",
    "driver_assigned", "driver_confirmed", "driver_pickup_arrival", "delivery_picked", "driver_dropoff_arrival",
    "completed", "dispatched", "delivered", "ready_for_dinning", "ready_for_takeway", "not-delivered"
  ])).sort();
  const [selectedStatus, setSelectedStatus] = useState("");

  // Search Tab
  const [orderIdInput, setOrderIdInput] = useState("");
  const [searchDetails, setSearchDetails] = useState({});
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchSelectedStatus, setSearchSelectedStatus] = useState("");
  const [searchStatusUpdating, setSearchStatusUpdating] = useState(false);

  // Filtering & pagination (table tab)
  const filteredOrders = Orders.filter((order) =>
    Object.values(order).some((v) => v?.toString().toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage) || 1;

  // API: Clients
  const FetchClientCall = async () => {
    setLoading(true);
    try {
      const payload = { userid: Admin.pkid, latitude, longitude };
      const res = await axios.post(`${baseUrl}/api/Admin/getAllClientList`, payload, config);
      if (res?.data?.responseCode === "00") setClients(res.data.response);
      else toast.error(res?.data?.responseMessage || "Failed to load clients");
    } catch {
      toast.error("Error while loading clients");
    } finally {
      setLoading(false);
    }
  };

  // API: Orders (table)
  const getNewOrder = async () => {
    try {
      if (firstLoad) setLoading(true);
      const body = {
        adminId: Admin.pkid,
        userid: userID || "",
        statuses: [
          "received", "paid", "accepted", "prepared", "rejected", "refunded",
          "driver_assigned", "driver_confirmed", "driver_pickup_arrival", "delivery_picked", "driver_dropoff_arrival",
        ],
        fromdate: startDateCurrent,
        todate: endDateCurrent,
        latitude, longitude,
      };
      const res = await axios.post(`${baseUrl}/api/Admin/getAllClientsCatalogueOrders`, body, config);
      setOrders(res?.data?.responseCode === "00" ? (res?.data?.response || []) : []);
    } catch (e) {
      console.error(e);
    } finally {
      if (firstLoad) { setLoading(false); setFirstLoad(false); }
    }
  };

  // API: Update payment status
  const updatePaymentStatus = async (orderid) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You want to update the payment status for this order?",
      icon: "question", showCancelButton: true,
      confirmButtonText: "Yes, Update", cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      const payload = { orderid };
      const res = await axios.post(`${baseUrl}/api/Order/v1.0/updateOrderPaymentStatus`, payload, config);
      if (res?.data?.responseCode === "00") {
        const d = res.data.response;
        Swal.fire({
          title: "Payment Status Updated!",
          html: `
            <b>Status:</b> ${d.status}<br/>
            <b>Ref ID:</b> <span style="font-weight:bold;">${d.refid}</span><br/>
            <b>Merchant ID:</b> <span style="font-weight:bold;">${d.utnno}</span><br/>
            <b>Amount:</b> ₹${d.transactionAmount}<br/>
            <b>Date:</b> ${d.createdOn}
          `,
          icon: "success",
        });
      } else Swal.fire("Failed", res?.data?.responseMessage || "Update failed", "error");
    } catch {
      Swal.fire("Error", "Something went wrong while updating status.", "error");
    }
  };

    // API:Request address 
  const RequestAddressApi = async (orderid) => {
    const result = await Swal.fire({
      title: "Do you want request for address?",
      text: "You want Request for Address for this order?",
      icon: "question", showCancelButton: true,
      confirmButtonText: "Yes, Update", cancelButtonText: "Cancel",
    });
    if (!result.isConfirmed) return;

    try {
      const payload = { orderid };
      const res = await axios.post(`${baseUrl}/api/Whatsapp/v1.0/locationRequest`, payload, config);
      if (res?.data?.responseCode === "00") {
        const d = res.data.response;
        Swal.fire({
          title: "Request Sent Successfully !",
          html: `
            <b>Status:</b> ${res.responseMessage}<br/>
          
          `,
          icon: "success",
        });
      } else Swal.fire("Failed", res?.data?.responseMessage || "Update failed", "error");
    } catch {
      Swal.fire("Error", "Something went wrong while updating status.", "error");
    }
  };
  // API: Send to delivery partner
// API: Send to delivery partner with paymentMode option
const sendToDeliveryPartner = async (orderid, clientUserId) => {
  const { value: paymentMode } = await Swal.fire({
    title: "Select Payment Mode",
    input: "select",
    inputOptions: {
      COD: "Cash on Delivery",
      "": "None (Prepaid/Null)",
    },
    inputPlaceholder: "Choose payment mode",
    showCancelButton: true,
  });

  if (paymentMode === undefined) return; // cancelled

  try {
    const payload = {
      userid: clientUserId,
      orderId: orderid,
      platform: "whatsapp",
      paymentMode: paymentMode || null,
      deliveryPartner: "packky",
    };

    const res = await axios.post(
      `${baseUrl}/api/Order/v1.0/assignDeliveryPartner`,
      payload,
      config
    );

    if (res?.data?.responseCode === "00") {
      Swal.fire("Sent!", "Order forwarded to delivery partner.", "success");
    } else {
      Swal.fire(
        "Failed",
        res?.data?.responseMessage || "Failed to send order.",
        "error"
      );
    }
  } catch {
    Swal.fire("Error", "Something went wrong.", "error");
  }
};

  // API: Order Details
  const orderDetailesAPI = async (orderid) => {
    setOrderDetailsLoader(true);
    try {
      const res = await axios.post(`${baseUrl}/api/Order/v1.0/getOrderDetails`, { orderid }, config);
      if (res?.data?.responseCode === "00") setParticularOrderDetails(res.data.response || {});
      else setParticularOrderDetails({});
    } catch (e) {
      console.error("Error making API call:", e);
      setParticularOrderDetails({});
    } finally {
      setOrderDetailsLoader(false);
    }
  };

  // Change Status (shared)
  const changeOrderStatus = async (source = "modal") => {
    const isSearch = source === "search";
    const details = isSearch ? searchDetails : particularOrderDetails;
    const chosenStatus = isSearch ? searchSelectedStatus : selectedStatus;
    const orderid = isSearch ? details?.orderid : currentOrderRow?.orderid;

    if (!orderid) return toast.error("Order id missing");
    if (!chosenStatus) return toast.error("Select a status first");

    const rowClient = currentOrderRow?.us_code;
    const detailsClient = details?.fk_user_id;
    const fallbackFilter = userID || "";
    const clientUserId = rowClient || detailsClient || fallbackFilter;
    if (!clientUserId) return toast.error("Client id (userid) missing");

    isSearch ? setSearchStatusUpdating(true) : setStatusUpdating(true);
    try {
      const requestBody = {
        userid: clientUserId,
        orderid,
        productid: "",
        status: chosenStatus,
        time: "",
        payment: "",
      };
      await axios.post(`${baseUrl}/api/Whatsapp/changeCatelogOrderStatus`, requestBody, config);

      toast.success(`Order status updated to "${chosenStatus}"`);
      await getNewOrder();
      if (isSearch) await fetchSearchDetails(orderid);
      else await orderDetailesAPI(orderid);
    } catch (error) {
      Swal.fire("Error!", "Something went wrong with updating the order status.", "error");
    } finally {
      isSearch ? setSearchStatusUpdating(false) : setStatusUpdating(false);
    }
  };

  // Modal open with row
  const openDetailsModal = async (orderRow) => {
    setCurrentOrderRow(orderRow);
    setSelectedStatus(orderRow?.status || "");
    setShowDetails(true);
    await orderDetailesAPI(orderRow.orderid);
  };

  // Fetch for Search Tab
  const fetchSearchDetails = async (oid) => {
    if (!oid) return;
    setSearchLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/api/Order/v1.0/getOrderDetails`, { orderid: oid }, config);
      if (res?.data?.responseCode === "00") {
        setSearchDetails(res.data.response || {});
        setSearchSelectedStatus((res.data.response?.status) || "");
        // toast.success("Order fetched");
      } else {
        setSearchDetails({});
        toast.error(res?.data?.responseMessage || "No order found");
      }
    } catch (e) {
      setSearchDetails({});
      toast.error("Error fetching order");
    } finally {
      setSearchLoading(false);
    }
  };
  // NEW: Refund handler used by the Search tab actions
  const handleRefund = async (orderObj) => {
    // amount: prefer pay_amount, else totalOrderPrice (truncate toward zero)
    const amount = Math.trunc(Number(orderObj?.pay_amount ?? orderObj?.totalOrderPrice ?? 0)).toString();

    // transaction id: use virtual_acc (required)
    const transactionId = String(orderObj?.virtual_acc || "");

    if (!transactionId) {
      toast.error("Missing transaction id (virtual account).");
      return;
    }

    const result = await Swal.fire({
      title: "Confirm Refund",
      html: `
      <div style="text-align:left">
        <div><b>Transaction ID:</b> ${transactionId}</div>
        <div><b>Amount:</b> ${amount}</div>
      </div>
    `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, refund",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      allowOutsideClick: false,
    });

    if (!result.isConfirmed) return;

    try {
      setRefundLoadingId(transactionId);

      const payload = JSON.stringify({
        userid: orderObj?.fk_user_id || userID || "",
        amount: amount,              // string
        transactionId: transactionId // string
      });

      const res = await fetch(`${baseUrl}/api/Transactions/v1.0/refundProccess`, {
        method: "POST",
        headers: {
          ...config.headers, // includes Content-Type & auth
        },
        body: payload,
        redirect: "follow",
      });

      const data = await res.json();

      if (data?.responseCode === "00") {
        await Swal.fire({
          title: "Refund Initiated",
          text: data?.responseMessage || "Refund processed successfully.",
          icon: "success",
        });
        // refresh the search panel so payment_status etc. are fresh
        if (orderObj?.orderid) await fetchSearchDetails(orderObj.orderid, false);
      } else {
        await Swal.fire({
          title: "Refund Failed",
          text: data?.responseMessage || "Unable to process refund.",
          icon: "error",
        });
      }
    } catch (e) {
      console.error(e);
      await Swal.fire({
        title: "Error",
        text: "Something went wrong while processing the refund.",
        icon: "error",
      });
    } finally {
      setRefundLoadingId(null);
    }
  };


  // Effects
  useEffect(() => {
    FetchClientCall();
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition((pos) => {
        setLatitude(pos.coords.latitude);
        setLongitude(pos.coords.longitude);
      });
    }
  }, []);

  useEffect(() => {
    if (startDate && endDate) getNewOrder();
  }, [startDate, endDate, userID]);

  // Compact styles for table tab
  const compactFont = { fontSize: "12px" };
  const cellPad = "p-1";
  const twoDecimalPlaces = (value) => {
    return Number(value).toFixed(2).toString();
  };
  const fetchRefunds = async () => {
    setRefundLoading(true);
    try {
      const res = await axios.post(
        `${baseUrl}/api/Transactions/v1.0/getRefundReport`,
        { userid: Admin.pkid },
        config
      );
      if (res?.data?.responseCode === "00") {
        setRefunds(res?.data?.response || []);
      } else {
        setRefunds([]);
        toast.error(res?.data?.responseMessage || "Failed to fetch refunds");
      }
    } catch (e) {
      console.error("fetchRefunds error:", e);
      toast.error("Error fetching refunds");
    } finally {
      setRefundLoading(false);
    }
  };
  // genrate payment link code
  // Generate Payment Intent
  const handleGeneratePaymentIntent = async (orderObj) => {
    if (!orderObj?.orderid) {
      toast.error("Order ID missing");
      return;
    }

    const payload = {
      userid: orderObj?.fk_user_id,
      orderId: orderObj?.orderid,
      custName: orderObj?.customer_name || "N/A",
      custMobile: orderObj?.customer_mobile || "",
    };

    try {
      const result = await Swal.fire({
        title: "Send Payment Link?",
        html: `
        <div style="text-align:left">
          <div><b>User ID:</b> ${payload.userid}</div>
          <div><b>Order ID:</b> ${payload.orderId}</div>
          <div><b>Customer:</b> ${payload.custName}</div>
          <div><b>Mobile:</b> ${payload.custMobile}</div>
        </div>
      `,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Yes, Send",
        cancelButtonText: "Cancel",
      });

      if (!result.isConfirmed) return;

      const res = await axios.post(
        `${baseUrl}/api/Order/generatePaymentIntent`,
        payload,
        config
      );

      if (res?.data?.responseCode === "00") {
        Swal.fire({
          title: "Payment Link Sent!",
          text: res?.data?.responseMessage || "Link generated successfully.",
          icon: "success",
        });
      } else {
        Swal.fire("Failed", res?.data?.responseMessage || "Error generating payment link.", "error");
      }
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Something went wrong while generating payment link.", "error");
    }
  };




  return (
    <div className="marginRight">
      <div className="p-2 bg-white" style={{ height: "100vh", fontFamily: "Nunito,sans-serif" }}>
        <NoIndex />

        <div className="px-2">
          <div className="isancharr-campaign-container mb-2 bg-light" style={{ padding: "8px" }}>
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="mb-0" style={{ fontSize: 18 }}>Order Controller</h2>
              <button className="isancharr-refresh-btn" title="Refresh" onClick={() => getNewOrder()} style={{ width: 36, height: 36 }}>
                <img src={refreshicon} alt="Refresh" />
              </button>
            </div>
            <hr className="my-2" />

            {/* Tabs */}
            <ul className="nav nav-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "table" ? "active" : ""}`}
                  onClick={() => setActiveTab("table")}
                >
                  Orders Table
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "search" ? "active" : ""}`}
                  onClick={() => setActiveTab("search")}
                >
                  Search by Order ID
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "refunds" ? "active" : ""}`}
                  onClick={() => { setActiveTab("refunds"); fetchRefunds(); }}
                >
                  Refund List
                </button>
              </li>

            </ul>
          </div>

          {/* ---------------- TAB CONTENT ---------------- */}
          <div className="tab-content">
            {/* ===== TAB 1: TABLE (dense) ===== */}
            <div className={`tab-pane fade ${activeTab === "table" ? "show active" : ""}`}>
              <div className="d-flex justify-content-start gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Universal Search..."
                  className="form-control"
                  style={{ maxWidth: 420, height: 34, ...compactFont }}
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
                <select
                  className="form-select"
                  style={{ width: 260, height: 34, ...compactFont }}
                  onChange={(e) => setuserID(e.target.value)}
                >
                  <option value="">Select Client</option>
                  {Clients.map((item, i) => (
                    <option key={i} value={item.us_code}>
                      {item.us_code} : {item.us_name}
                    </option>
                  ))}
                </select>
              </div>

              {Loading ? (
                <div className="m-4 p-4 d-flex justify-content-center">
                  <div className="d-flex flex-column align-items-center justify-content-center">
                    <div className="p-2"><div className="loader"></div></div>
                    <div className="p-2"><img src={loader} alt="" height={32} /></div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="scrollable-table" style={{ overflow: "auto", maxHeight: "74vh" }}>
                    <table className="table table-bordered table-hover table-sm align-middle" style={{ ...compactFont }}>
                      <thead>
                        <tr className="text-center greenColour">
                          <th className={cellPad}>Restaurant</th>
                          <th className={cellPad}>Order #</th>
                          <th className={cellPad}>Amount</th>
                          <th className={cellPad}>Type</th>
                          <th className={cellPad}>Order Status</th>
                          <th className={cellPad}>Payment Status</th>
                          <th className={cellPad}>Delivery Status</th>
                          <th className={cellPad}>Payment Mode</th>
                          <th className={cellPad}>Customer #</th>
                          <th className={cellPad} style={{ minWidth: 300 }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentOrders.map((order, idx) => (
                          <tr key={idx} className="text-center">
                            <td className={cellPad}>{order.organization_name || "N/A"}</td>
                            <td className={cellPad}>{order.orderid || "N/A"}</td>
                            <td className={cellPad}>₹{twoDecimalPlaces(order.totalOrderPrice) ?? "N/A"}</td>
                            <td className={cellPad}>{order.orderType || "N/A"}</td>
                            <td className={cellPad}>{order.status || "N/A"}</td>
                            <td className={cellPad}>{order.payment_status || "N/A"}</td>
                            <td className={cellPad}>{order.delivery_partner_status || "N/A"}</td>
                            <td className={cellPad}>{order.payment_mode || "N/A"}</td>
                            <td className={cellPad}>{order.customer_mobile || "N/A"}</td>
                           <td className={`text-nowrap ${cellPad}`}>
  <button
    className="btn btn-sm btn-outline-dark me-1"
    onClick={() => openDetailsModal(order)}
  >
    Details
  </button>
  <button
    className="btn btn-sm btn-outline-primary me-1"
    onClick={() => updatePaymentStatus(order.orderid)}
  >
    Update Pay
  </button>
  <button
    className="btn btn-sm btn-outline-warning me-1"
    onClick={() => handleGeneratePaymentIntent(order)}
  >
    Payment Link
  </button>
  {order.orderType === "home_delivery" && (
    <button
      className="btn btn-sm btn-outline-success"
      onClick={() => sendToDeliveryPartner(order.orderid, order.us_code)}
    >
      Send to DP
    </button>
  )}
</td>

                          </tr>
                        ))}
                        {currentOrders.length === 0 && (
                          <tr><td colSpan="10" className="text-center p-3">No orders found.</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mt-2" style={compactFont}>
                    <span>Page {currentPage} of {totalPages}</span>
                    <div>
                      <button
                        className="btn btn-sm btn-secondary me-2"
                        onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* ===== TAB 2: SEARCH BY ORDER ID (improved) ===== */}
            <div className={`tab-pane fade ${activeTab === "search" ? "show active" : ""}`}>
              <div className="aoc-oi">
                {/* Input row */}
                <div className="aoc-oi-inputrow">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter Order ID"
                    value={orderIdInput}
                    onChange={(e) => setOrderIdInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") fetchSearchDetails(orderIdInput); }}
                  />
                  <button
                    className="aoc-btn aoc-btn-primary"
                    onClick={() => fetchSearchDetails(orderIdInput)}
                    disabled={!orderIdInput || searchLoading}
                  >
                    {searchLoading ? <><span className="spinner-border spinner-border-sm me-1" /> Fetching...</> : "Fetch Order"}
                  </button>
                </div>

                {/* Loading / Empty */}
                {searchLoading ? (
                  <div className="d-flex align-items-center gap-2 my-3">
                    <span className="spinner-border spinner-border-sm" />
                    Loading...
                  </div>
                ) : Object.keys(searchDetails || {}).length === 0 ? (
                  <div className="aoc-oi-empty aoc-mt">No data yet. Search an Order ID.</div>
                ) : (
                  <>
                    {/* Header / Action Bar */}
                    <div className="aoc-oi-header aoc-gap-y">
                      <div className="aoc-oi-title">
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <h6>Order #{searchDetails.orderid}</h6>
                          <div className="aoc-oi-badges">
                            <span className={`aoc-oi-badge ${searchDetails.status === "received" ? "warning" : "secondary"}`}>
                              {searchDetails.status || "N/A"}
                            </span>
                            <span className="aoc-oi-badge info">{searchDetails.orderType || "N/A"}</span>
                            <span className="aoc-oi-badge secondary">
                              {searchDetails.payment_status || "N/A"} ({searchDetails.payment_mode || "N/A"})
                            </span>
                            {searchDetails.delivery_partner_status && (
                              <span className="aoc-oi-badge">DP: {searchDetails.delivery_partner_status}</span>
                            )}
                            {searchDetails.refund_status === "Y" ? (
                              <span className="aoc-oi-badge bg-danger text-white">Refunded</span>
                            ) : ''}
                          </div>
                        </div>
                        <div className="aoc-oi-subtle">
                          Order Date: {searchDetails.order_date || "N/A"} · Updated: {searchDetails.update || "N/A"}
                        </div>
                      </div>

                      <div className="aoc-oi-actions d-flex flex-wrap gap-2">
                        <button
                          className="btn btn-sm btn-outline-primary flex-fill d-flex align-items-center justify-content-center"
                          style={{ minWidth: "150px" }}
                          onClick={() => updatePaymentStatus(searchDetails.orderid)}
                        >
                          <CreditCard size={16} className="me-1" />
                          Update Payment
                        </button>

                        <button
                          className="btn btn-sm btn-outline-warning flex-fill d-flex align-items-center justify-content-center"
                          style={{ minWidth: "150px" }}
                          onClick={() => handleGeneratePaymentIntent(searchDetails)}
                        >
                          <Link2 size={16} className="me-1" />
                          Payment Link
                        </button>

                        {searchDetails.orderType === "home_delivery" && (
                          <button
                            className="btn btn-sm btn-outline-success flex-fill d-flex align-items-center justify-content-center"
                            style={{ minWidth: "150px" }}
                            onClick={() =>
                              sendToDeliveryPartner(
                                searchDetails.orderid,
                                searchDetails.fk_user_id || userID || ""
                              )
                            }
                          >
                            <Truck size={16} className="me-1" />
                            Send to DP
                          </button>
                        )}

                        <button
                          className="btn btn-sm btn-outline-danger flex-fill d-flex align-items-center justify-content-center"
                          style={{ minWidth: "150px" }}
                          onClick={() => handleRefund(searchDetails)}
                          disabled={
                            String(searchDetails.payment_status || "").toLowerCase() === "unpaid" ||
                            !searchDetails.virtual_acc ||
                            refundLoadingId === String(searchDetails.virtual_acc) ||
                            searchDetails.refund_status === "Y"
                          }
                          title={
                            String(searchDetails.payment_status || "").toLowerCase() === "unpaid"
                              ? "Refund disabled: payment is unpaid"
                              : !searchDetails.virtual_acc
                                ? "Refund disabled: no transaction id"
                                : "Refund"
                          }
                        >
                          {refundLoadingId === String(searchDetails.virtual_acc) ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" />
                              Refunding...
                            </>
                          ) : (
                            <>
                              <RotateCcw size={16} className="me-1" />
                              Refund Pay
                            </>
                          )}
                        </button>
                         <button
                          className="btn btn-sm btn-outline-secondary flex-fill d-flex align-items-center justify-content-center"
                          style={{ minWidth: "150px" }}
                          onClick={() => RequestAddressApi(searchDetails.orderid)}
                        >
                          <HelpCircle size={16} className="me-1" />
                          Request Add.
                        </button>

                        {/* Status change section */}
                        <div className="d-flex flex-column flex-grow-1" style={{ minWidth: "220px" }}>
                          <div className="aoc-oi-status-label fw-semibold mb-1">Change Status</div>
                          <div className="d-flex gap-2">
                            <select
                              className="form-select form-select-sm flex-fill"
                              value={searchSelectedStatus}
                              onChange={(e) => setSearchSelectedStatus(e.target.value)}
                            >
                              <option value="">Select status</option>
                              {ALL_STATUSES.map((s) => (
                                <option key={s} value={s}>
                                  {s}
                                </option>
                              ))}
                            </select>
                            <button
                              className="btn btn-sm btn-primary d-flex align-items-center"
                              onClick={() => changeOrderStatus("search")}
                              disabled={searchStatusUpdating || !searchSelectedStatus}
                            >
                              {searchStatusUpdating ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-1" />
                                  Updating...
                                </>
                              ) : (
                                "Update"
                              )}
                            </button>
                          </div>
                        </div>
                      </div>

                    </div>

                    {/* Summary & Address */}
                    <div className="row g-3 aoc-mt">
                      <div className="col-12 col-lg-6">
                        <div className="aoc-card aoc-kv">
                          <div className="aoc-card-title">Summary</div>
                          <div><b>Total:</b> ₹{fmt(searchDetails.totalOrderPrice)}</div>
                          <div><b>Subtotal:</b> ₹{fmt(searchDetails.subtotal)}</div>
                          <div><b>Platform:</b> {searchDetails.platform || "N/A"}</div>
                          <div><b>Virtual Acc:</b> {searchDetails.virtual_acc || "N/A"}</div>
                          <div><b>GST Claim:</b> {searchDetails.gst_claim || "N/A"}</div>
                          <div><b>Loyalty Points:</b> {searchDetails.loyalty_points || "0"}</div>
                        </div>
                      </div>

                      <div className="col-12 col-lg-6">
                        <div className="aoc-card aoc-kv">
                          <div className="aoc-card-title">Customer & Address</div>
                          <div><b>Name:</b> {searchDetails.customer_name || "N/A"}</div>
                          <div><b>Phone:</b> {searchDetails.customer_mobile || "N/A"}</div>
                          <div className="aoc-oi-subtle">{searchDetails.address_line}</div>
                          {(() => {
                            const a = safeParse(searchDetails?.address_json).values || {};
                            return (
                              <div className="aoc-oi-subtle">
                                {a?.address || ""} {a?.landmark_area || ""} {a?.building_name || ""} {a?.floor_number || ""}{" "}
                                {a?.tower_number || ""} {a?.city || ""} {a?.state || ""} {a?.in_pin_code || ""}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>

                    {/* Charges / Taxes */}
                    <div className="row g-3 aoc-mt">
                      <div className="col-12 col-lg-6">
                        <div className="aoc-card aoc-kv">
                          <div className="aoc-card-title">Charges & Discounts</div>
                          <div><b>Delivery Fee:</b> ₹{fmt(searchDetails.delivery_fee)}</div>
                          <div><b>Other Fee:</b> ₹{fmt(searchDetails.other_fee)}</div>
                          <div><b>Discount %:</b> {fmt(searchDetails.discount_percentage)}%</div>
                          <div><b>Discount Amt:</b> ₹{fmt(searchDetails.discount_amount)}</div>
                        </div>
                      </div>
                      <div className="col-12 col-lg-6">
                        <div className="aoc-card aoc-kv">
                          <div className="aoc-card-title">Taxes</div>
                          <div><b>GST:</b> ₹{fmt(searchDetails.gst)}</div>
                          <div><b>SGST:</b> ₹{fmt(searchDetails.tax_sgst)}</div>
                          <div><b>CGST:</b> ₹{fmt(searchDetails.tax_cgst)}</div>
                          <div><b>Order GST:</b> ₹{fmt(searchDetails.order_tax_gst)}</div>
                        </div>
                      </div>
                    </div>

                    {/* Items with Customizations */}
                    <div className="aoc-table aoc-mt">
                      <table className="table table-bordered table-sm align-middle" style={{ margin: 0 }}>
                        <thead>
                          <tr className="text-center">
                            <th className="p-2">#</th>
                            <th className="p-2">Product</th>
                            <th className="p-2">Qty</th>
                            <th className="p-2">Item Price</th>
                            <th className="p-2">Total</th>
                            <th className="p-2" style={{ minWidth: 260 }}>Customization</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(searchDetails.items || []).map((it, i) => {
                            const pairs = parseCustomization(it.customization);
                            return (
                              <tr key={`${it.id || it.product_id}-${i}`} className="text-center">
                                <td className="p-2">{i + 1}</td>
                                <td className="p-2">{it.product_id || it.item_name || "—"}</td>
                                <td className="p-2">{it.quantity ?? "—"}</td>
                                <td className="p-2">₹{fmt(it.item_price)}</td>
                                <td className="p-2">₹{fmt(it.total_price)}</td>
                                <td className="p-2 text-start">
                                  {pairs.length === 0 ? (
                                    <span className="text-muted">—</span>
                                  ) : (
                                    <div className="aoc-chips">
                                      {pairs.map(([k, v]) => (
                                        <span key={`${k}-${v}-${i}`} className="aoc-chip">
                                          <b>{k}:</b> {String(v)}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                          {(searchDetails.items || []).length === 0 && (
                            <tr><td className="text-center" colSpan="6">No items</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    {/* Notes (if any) */}
                    {(searchDetails.vendor_response || searchDetails.kitchen_note || searchDetails.remark) && (
                      <div className="aoc-card aoc-kv aoc-mt">
                        <div className="aoc-card-title">Notes</div>
                        {searchDetails.vendor_response && (<div><b>Vendor Response:</b> {searchDetails.vendor_response}</div>)}
                        {searchDetails.kitchen_note && (<div><b>Kitchen Note:</b> {searchDetails.kitchen_note}</div>)}
                        {searchDetails.remark && (<div><b>Remark:</b> {searchDetails.remark}</div>)}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* ===== TAB 3: REFUND LIST ===== */}
            <div className={`tab-pane fade ${activeTab === "refunds" ? "show active" : ""}`}>
              {refundLoading ? (
                <div className="m-4 p-4 text-center">
                  <span className="spinner-border spinner-border-sm" /> Loading refunds...
                </div>
              ) : refunds.length === 0 ? (
                <div className="p-3 text-center text-muted">No refund records found.</div>
              ) : (
                <div className="scrollable-table" style={{ overflow: "auto", maxHeight: "74vh" }}>
                  <table className="table table-bordered table-sm table-hover align-middle" style={{ fontSize: "12px" }}>
                    <thead className="text-center greenColour">
                      <tr>
                        <th>ID</th>
                        <th>User ID</th>
                        <th>VA Account</th>
                        <th>Sender Acc</th>
                        <th>Created On</th>
                        <th>Updated</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Bank RRN</th>
                        <th>Mode</th>
                        <th>Order ID</th>
                        <th>Customer Mobile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {refunds.map((r, idx) => (
                        <tr key={idx} className="text-center">
                          <td>{r.id}</td>
                          <td>{r.userid}</td>
                          <td>{r.va_account}</td>
                          <td>{r.sender_acc || "N/A"}</td>
                          <td>{r.created_on}</td>
                          <td>{r.update}</td>
                          <td>{r.status}</td>
                          <td>₹{Number(r.amount).toFixed(2)}</td>
                          <td>{r.bank_rrn || "N/A"}</td>
                          <td>{r.mode}</td>
                          <td>{r.order_id || "N/A"}</td>
                          <td>{r.customer_mobile || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        </div>
      </div>

      {/* ---- Modal for table rows (kept, compact) ---- */}
      <div
        className={`modal fade ${showDetails ? "show d-block" : ""}`}
        tabIndex="-1"
        role="dialog"
        style={{ background: showDetails ? "rgba(0,0,0,0.35)" : "transparent" }}
        aria-modal={showDetails ? "true" : undefined}
      >
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content" style={{ fontSize: "12px" }}>
            <div className="modal-header py-2">
              <h5 className="modal-title m-0">Order Details</h5>
              <button type="button" className="btn-close" onClick={() => setShowDetails(false)} />
            </div>
            <div className="modal-body">
              {orderDetailsLoader ? (
                <div className="d-flex align-items-center gap-2">
                  <span className="spinner-border spinner-border-sm" />
                  Loading details...
                </div>
              ) : Object.keys(particularOrderDetails || {}).length === 0 ? (
                <div className="text-muted">No details available.</div>
              ) : (
                <>
                  <div className="row g-2">
                    <div className="col-6">
                      <div><b>Order #:</b> {particularOrderDetails.orderid || "N/A"}</div>
                      <div><b>Type:</b> {particularOrderDetails.orderType || "N/A"}</div>
                      <div><b>Status:</b> {particularOrderDetails.status || "N/A"}</div>
                      <div><b>Payment:</b> {particularOrderDetails.payment_status || "N/A"} ({particularOrderDetails.payment_mode || "N/A"})</div>
                    </div>
                    <div className="col-6">
                      <div><b>Order Date:</b> {particularOrderDetails.order_date || "N/A"}</div>
                      <div><b>Updated:</b> {particularOrderDetails.update || "N/A"}</div>
                      <div><b>Total:</b> ₹{fmt(particularOrderDetails.totalOrderPrice)}</div>
                      <div><b>Subtotal:</b> ₹{fmt(particularOrderDetails.subtotal)}</div>
                    </div>
                  </div>
                  <hr className="my-2" />
                  {(() => {
                    const a = safeParse(particularOrderDetails?.address_json).values || {};
                    return (
                      <div className="mb-2">
                        <b>Customer:</b> {particularOrderDetails.customer_name || "N/A"}{" "}
                        <span className="text-muted">({particularOrderDetails.customer_mobile || "N/A"})</span>
                        <div className="text-muted mt-1">
                          <div>{particularOrderDetails.address_line || ""}</div>
                          <div>
                            {a?.address || ""} {a?.landmark_area || ""} {a?.building_name || ""}{" "}
                            {a?.floor_number || ""} {a?.tower_number || ""} {a?.city || ""} {a?.state || ""} {a?.in_pin_code || ""}
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  <div className="table-responsive">
                    <table className="table table-bordered table-sm">
                      <thead>
                        <tr className="text-center">
                          <th className="p-1">#</th>
                          <th className="p-1">Product</th>
                          <th className="p-1">Qty</th>
                          <th className="p-1">Item Price</th>
                          <th className="p-1">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(particularOrderDetails.items || []).map((it, i) => (
                          <tr key={`${it.id || it.product_id}-${i}`} className="text-center">
                            <td className="p-1">{i + 1}</td>
                            <td className="p-1">{it.product_id || it.item_name || "—"}</td>
                            <td className="p-1">{it.quantity ?? "—"}</td>
                            <td className="p-1">₹{fmt(it.item_price)}</td>
                            <td className="p-1">₹{fmt(it.total_price)}</td>
                          </tr>
                        ))}
                        {(particularOrderDetails.items || []).length === 0 && (
                          <tr><td className="text-center" colSpan="5">No items</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="modal-footer py-2">
              <div className="d-flex flex-column flex-md-row w-100 gap-2 align-items-stretch align-items-md-center">
                <div className="d-flex flex-column flex-sm-row gap-1 gap-sm-2 flex-grow-1">
                  <div className="small text-muted pt-1" style={{ minWidth: 110 }}>Change Status</div>
                  <div className="flex-grow-1" style={{ minWidth: 220, maxWidth: 520 }}>
                    <select
                      className="form-select form-select-sm"
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                      <option value="">Select status</option>
                      {ALL_STATUSES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="d-flex flex-wrap gap-2 ms-md-auto">
                  <button
                    className="btn btn-sm btn-primary"
                    onClick={() => changeOrderStatus("modal")}
                    disabled={statusUpdating || !selectedStatus}
                  >
                    {statusUpdating ? (<><span className="spinner-border spinner-border-sm me-1" />Updating...</>) : "Update Status"}
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setShowDetails(false)}>Close</button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      {showDetails && (<div onClick={() => setShowDetails(false)} style={{ position: "fixed", inset: 0, zIndex: 1040 }} />)}

      <ToastContainer />
    </div>
  );
}

export default AdminOrderController;
